// Consolidated Timesheet PDF generator
// Shows all employees' timesheets for selected project(s) in a consolidated format

import logoImage from '../assets/logo.png';

export async function exportConsolidatedTimesheetPdf(data, options = {}) {
  const {
    projectName = 'All Projects',
    startDate,
    endDate,
    fileName,
    selectedProjectId = null, // If set, only show entries for this project
    returnBlob = false, // If true, return blob instead of downloading
  } = options;

  // Calculate number of days to determine orientation
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end dates
  
  // Use portrait for weekly (≤7 days), landscape for monthly (>7 days)
  const orientation = diffDays <= 7 ? 'portrait' : 'landscape';

  const JSPDFCtor = await loadJsPDF();
  await ensureAutoTableLoaded();
  const doc = new JSPDFCtor({ unit: 'pt', format: 'a4', orientation });
  
  // Load logo as base64 data URL
  const logoDataUrl = await loadLogoAsDataUrl(logoImage);

  renderConsolidatedReport(doc, data, projectName, startDate, endDate, logoDataUrl, selectedProjectId, orientation);

  if (returnBlob) {
    // Return blob URL for display
    const pdfBlob = doc.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);
    return blobUrl;
  } else {
    // Download the PDF
    const defaultName = `Consolidate_Timesheet_${projectName.replace(/\s+/g, '_')}_${safeDate(startDate)}.pdf`;
    doc.save(fileName || defaultName);
  }
}

async function loadJsPDF() {
  try {
    const m = await import('jspdf');
    return m.jsPDF || m.default;
  } catch (e1) {
    const m2 = await import('jspdf/dist/jspdf.umd.min.js');
    return m2.jsPDF || m2.default;
  }
}

async function ensureAutoTableLoaded() {
  await import('jspdf-autotable');
}

async function loadLogoAsDataUrl(imageSrc) {
  try {
    const response = await fetch(imageSrc);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Failed to load logo image:', error);
    return null;
  }
}

function safeDate(d) {
  if (!d) return '';
  return new Date(d).toISOString().slice(0, 10);
}

function buildWeekDays(startDate, endDate) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  // Set to start of day to avoid timezone issues
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0); // Set end to start of day to ensure exact count
  
  const days = [];
  // Calculate number of days between start and end (inclusive)
  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start and end
  
  for (let i = 0; i < diffDays; i++) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    day.setHours(0, 0, 0, 0);
    days.push(day);
  }
  return days;
}

function normalizeHours(entry = {}) {
  if (typeof entry.hours === 'number') return entry.hours;
  if (typeof entry.hoursWorked === 'number') return entry.hoursWorked;
  const parsedHours = parseFloat(entry.hours);
  if (!Number.isNaN(parsedHours)) return parsedHours;
  const parsedWorked = parseFloat(entry.hoursWorked);
  if (!Number.isNaN(parsedWorked)) return parsedWorked;
  return 0;
}

function getApprovalStatus(timesheet) {
  if (!timesheet) return 'Pending/Reject/Approval';
  const status = timesheet.status || '';
  if (status === 'APPROVED') return 'Approved';
  if (status === 'REJECTED') return 'Rejected';
  return 'Pending';
}

function renderConsolidatedReport(doc, data, projectName, startDate, endDate, logoDataUrl, selectedProjectId = null, orientation = 'portrait') {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const left = 40;
  const right = pageWidth - 40;
  const mm = (v) => v;

  // Build data structure
  const days = buildWeekDays(startDate, endDate);
  
  // Get month for landscape mode (use first day's month)
  const monthForLandscape = days.length > 0 ? days[0].toLocaleDateString(undefined, { month: 'short' }) : '';
  
  // Create a set of valid date keys for quick lookup
  const dayKeysSet = new Set();
  days.forEach(day => {
    const year = day.getFullYear();
    const month = String(day.getMonth() + 1).padStart(2, '0');
    const dayNum = String(day.getDate()).padStart(2, '0');
    const dateKey = `${year}-${month}-${dayNum}`;
    dayKeysSet.add(dateKey);
  });
  
  // Helper function to extract project info from entry
  function getProjectInfo(entry) {
    if (typeof entry.project === 'object' && entry.project?.id) {
      return {
        id: entry.project.id,
        name: entry.project.name || `Project ${entry.project.id}`
      };
    }
    if (entry.projectId) {
      return {
        id: typeof entry.projectId === 'object' ? entry.projectId.id : parseInt(entry.projectId),
        name: entry.projectName || `Project ${entry.projectId}`
      };
    }
    if (typeof entry.project === 'string') {
      const projectId = parseInt(entry.project);
      return {
        id: projectId,
        name: entry.projectName || `Project ${projectId}`
      };
    }
    return null;
  }
  
  // Group entries by project
  const projectData = {};
  
  (data.timesheets || []).forEach(timesheet => {
    const employeeName = `${timesheet.employee?.firstName || ''} ${timesheet.employee?.lastName || ''}`.trim() || 'Unknown';
    
    (timesheet.entries || []).forEach(entry => {
      const projectInfo = getProjectInfo(entry);
      if (!projectInfo) return; // Skip entries without project
      
      // If a specific project is selected, only include entries for that project
      if (selectedProjectId !== null && projectInfo.id !== selectedProjectId) {
        return;
      }
      
      const projectId = projectInfo.id;
      const projectNameForTable = projectInfo.name;
      
      // Initialize project if not exists
      if (!projectData[projectId]) {
        projectData[projectId] = {
          id: projectId,
          name: projectNameForTable,
          employees: {}
        };
      }
      
      // Initialize employee if not exists
      if (!projectData[projectId].employees[employeeName]) {
        projectData[projectId].employees[employeeName] = {
          name: employeeName,
          dailyHours: {},
          totalHours: 0,
          status: getApprovalStatus(timesheet),
          timesheetId: timesheet.id
        };
      }
      
      // Parse date
      let dateStr;
      let rawDate = entry.date || entry.entryDate;
      
      if (!rawDate) return;
      
      if (typeof rawDate === 'string') {
        if (rawDate.match(/^\d{4}-\d{2}-\d{2}/)) {
          dateStr = rawDate.split('T')[0].split(' ')[0];
        } else {
          const entryDate = new Date(rawDate);
          if (!isNaN(entryDate.getTime())) {
            const year = entryDate.getFullYear();
            const month = String(entryDate.getMonth() + 1).padStart(2, '0');
            const day = String(entryDate.getDate()).padStart(2, '0');
            dateStr = `${year}-${month}-${day}`;
          } else {
            return;
          }
        }
      } else {
        const entryDate = new Date(rawDate);
        if (!isNaN(entryDate.getTime())) {
          const year = entryDate.getFullYear();
          const month = String(entryDate.getMonth() + 1).padStart(2, '0');
          const day = String(entryDate.getDate()).padStart(2, '0');
          dateStr = `${year}-${month}-${day}`;
        } else {
          return;
        }
      }
      
      const hours = normalizeHours(entry);
      if (hours <= 0) return;
      
      // Only process hours if the date is within our date range
      if (dayKeysSet.has(dateStr)) {
        const emp = projectData[projectId].employees[employeeName];
        if (!emp.dailyHours[dateStr]) {
          emp.dailyHours[dateStr] = 0;
        }
        emp.dailyHours[dateStr] += hours;
        emp.totalHours += hours;
      }
    });
  });
  
  // Group days by month (for multi-month date ranges)
  const groupDaysByMonth = (dayList) => {
    const monthGroups = {};
    dayList.forEach(day => {
      const year = day.getFullYear();
      const month = day.getMonth();
      const key = `${year}-${month}`;
      if (!monthGroups[key]) {
        monthGroups[key] = {
          days: [],
          monthName: day.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
          monthShort: day.toLocaleDateString(undefined, { month: 'short' })
        };
      }
      monthGroups[key].days.push(day);
    });
    // Sort by date to maintain chronological order
    return Object.keys(monthGroups).sort().map(key => monthGroups[key]);
  };
  
  // For landscape mode with more than 15 days OR multiple months, split by month
  const monthGroups = groupDaysByMonth(days);
  const shouldSplitByMonth = monthGroups.length > 1;
  const shouldSplitBy15Days = orientation === 'landscape' && days.length > 15 && !shouldSplitByMonth;
  
  // Build date headers with month inside for landscape
  const buildDateHeaders = (dayList) => {
    if (orientation === 'landscape') {
      return dayList.map(d => {
        const month = d.toLocaleDateString(undefined, { month: 'short' });
        const day = d.toLocaleDateString(undefined, { day: '2-digit' });
        return `${month} ${day}`;
      });
    } else {
      return dayList.map(d => d.toLocaleDateString(undefined, { day: '2-digit' }));
    }
  };
  
  // Build header rows - for landscape: dates with month (no Task), for portrait: months and dates (with Task)
  const buildHeaderRows = (dayList) => {
    if (orientation === 'landscape') {
      const dateHeadersWithMonth = buildDateHeaders(dayList);
      return {
        headerRow1: ['Name', ...dateHeadersWithMonth, 'Total', 'Approval Status'],
        headerRow2: []
      };
    } else {
      const monthHeaders = dayList.map(d => d.toLocaleDateString(undefined, { month: 'short' }));
      const dateHeaders = dayList.map(d => d.toLocaleDateString(undefined, { day: '2-digit' }));
      return {
        headerRow1: ['Name', ...monthHeaders, 'Total', 'Task', 'Approval Status'],
        headerRow2: ['', ...dateHeaders, '', '', '', '']
      };
    }
  };
  
  // Get sorted project IDs
  const projectIds = Object.keys(projectData).sort((a, b) => {
    return projectData[a].name.localeCompare(projectData[b].name);
  });
  
  let currentY = 235; // Start position for first table
  let isFirstTable = true;
  
  // Helper function to build table rows for a subset of days
  const buildTableRowsForDays = (dayList, employeeNames, proj) => {
    const tableRows = [];
    
    employeeNames.forEach(empName => {
      const emp = proj.employees[empName];
      const row = [empName];
      
      let rowTotal = 0;
      
      dayList.forEach(day => {
        const year = day.getFullYear();
        const month = String(day.getMonth() + 1).padStart(2, '0');
        const dayNum = String(day.getDate()).padStart(2, '0');
        const dayKey = `${year}-${month}-${dayNum}`;
        const hours = emp.dailyHours[dayKey] || 0;
        rowTotal += hours;
        row.push(hours > 0 ? hours.toFixed(2) : '0.00');
      });
      
      row.push(rowTotal.toFixed(2));
      // In landscape mode, skip Task column, only show Approval Status
      if (orientation !== 'landscape') {
        row.push('View');
      }
      row.push(emp.status);
      
      tableRows.push(row);
    });
    
    // Calculate totals row
    const totalsRow = ['Total'];
    let grandTotal = 0;
    
    dayList.forEach(day => {
      const year = day.getFullYear();
      const month = String(day.getMonth() + 1).padStart(2, '0');
      const dayNum = String(day.getDate()).padStart(2, '0');
      const dayKey = `${year}-${month}-${dayNum}`;
      let dayTotal = 0;
      employeeNames.forEach(empName => {
        const emp = proj.employees[empName];
        dayTotal += emp.dailyHours[dayKey] || 0;
      });
      totalsRow.push(dayTotal.toFixed(2));
      grandTotal += dayTotal;
    });
    
    totalsRow.push(grandTotal.toFixed(2));
    // In landscape mode, skip Task column, only show Approval Status
    if (orientation !== 'landscape') {
      totalsRow.push('-');
    }
    totalsRow.push('-');
    
    return { tableRows, totalsRow };
  };
  
  // Render a table for each project
  projectIds.forEach((projectId, projectIndex) => {
    const proj = projectData[projectId];
    const employeeNames = Object.keys(proj.employees).sort();
    
    if (employeeNames.length === 0) return; // Skip projects with no employees
    
    // Determine which day ranges to render - prioritize month grouping
    let dayRanges = [];
    if (shouldSplitByMonth) {
      // Group by month - each month gets its own table
      dayRanges = monthGroups.map(monthGroup => ({
        days: monthGroup.days,
        label: monthGroup.monthName,
        monthShort: monthGroup.monthShort
      }));
    } else if (shouldSplitBy15Days) {
      // Split by 15 days if single month but more than 15 days
      const firstHalfDays = days.slice(0, 15);
      const secondHalfDays = days.slice(15);
      dayRanges = [
        { days: firstHalfDays, label: 'First 15 Days', monthShort: monthGroups[0]?.monthShort || '' },
        { days: secondHalfDays, label: 'Remaining Days', monthShort: monthGroups[0]?.monthShort || '' }
      ];
    } else {
      // Single table for all days
      dayRanges = [{ days: days, label: '', monthShort: monthGroups[0]?.monthShort || '' }];
    }
    
    // Render table(s) for this project
    dayRanges.forEach((dayRange, rangeIndex) => {
      if (dayRange.days.length === 0) return; // Skip empty ranges
      
      const { tableRows, totalsRow } = buildTableRowsForDays(dayRange.days, employeeNames, proj);
      const { headerRow1, headerRow2 } = buildHeaderRows(dayRange.days);
    
      // Estimate table height: header (1 or 2 rows) + body rows + totals row + padding
      const headerRows = orientation === 'landscape' ? 1 : 2;
      const estimatedTableHeight = (headerRows + tableRows.length + 1) * 25 + 60;
      const bottomMargin = 30; // Reduced margin to fit more on page
      const availableSpace = pageHeight - currentY - bottomMargin;
      
      // Check if we need a new page - be more conservative, only break if really necessary
      const isFirstTableInProject = isFirstTable && rangeIndex === 0;
      // Only go to new page if we're very close to bottom OR table definitely won't fit
      const needsNewPage = currentY > pageHeight - 80 || (!isFirstTableInProject && estimatedTableHeight > availableSpace + 50);
      
      if (needsNewPage && !isFirstTableInProject) {
        doc.addPage();
        currentY = 80; // Start higher on page (no full header)
        // Only show project name, not full header
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(0);
        const projectDisplayName = orientation === 'landscape' 
          ? `Project Name: ${proj.name} (${dayRange.monthShort || monthForLandscape})`
          : `Project Name: ${proj.name}`;
        doc.text(projectDisplayName, left, currentY);
        currentY += 20; // Space after project name
      } else if (isFirstTableInProject) {
        // Draw header for first table only
        const projectDisplayName = orientation === 'landscape' 
          ? `${proj.name} (${dayRange.monthShort || monthForLandscape})`
          : proj.name;
        drawHeader(doc, projectDisplayName, startDate, endDate, logoDataUrl, left, right, mm, orientation);
      } else {
        // For subsequent tables on same page, add project name label
        // Ensure we have proper spacing from previous table
        if (currentY < 235) {
          currentY = 235; // Minimum start position
        }
        currentY += 15; // Reduced spacing before next table
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(0);
        const projectDisplayName = orientation === 'landscape' 
          ? `Project Name: ${proj.name} (${dayRange.monthShort || monthForLandscape})`
          : `Project Name: ${proj.name}`;
        doc.text(projectDisplayName, left, currentY);
        currentY += 12; // Reduced space after project name
      }
      
      if (isFirstTable) {
        isFirstTable = false;
      }
    
    // Draw table for this project
    const tableResult = doc.autoTable({
      startY: currentY,
      head: orientation === 'landscape' ? [headerRow1] : [headerRow1, headerRow2],
      body: [...tableRows, totalsRow],
      theme: 'grid',
      showHead: 'everyPage', // Show header on every page if table spans multiple pages
      styles: {
        font: 'helvetica',
        fontSize: orientation === 'landscape' ? 8 : 9,
        cellPadding: orientation === 'landscape' ? 4 : 5,
        textColor: 0,
        fillColor: [255, 255, 255]
      },
      headStyles: {
        fillColor: [245, 247, 250],
        textColor: 0,
        halign: 'center',
        fontStyle: 'bold',
        fontSize: orientation === 'landscape' ? 9 : 10
      },
      bodyStyles: {
        fillColor: [250, 250, 252],
        textColor: 0,
        halign: 'center'
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
        textColor: 0
      },
      columnStyles: (() => {
        const baseStyles = {
          0: { halign: 'left' } // Name column
        };
        
        if (orientation === 'landscape') {
          // Landscape: no Task column, only Approval Status
          const numDateColumns = dayRange.days.length;
          const fixedColumnsWidth = 40 + 80; // Total + Approval Status (no Task)
          const nameColumnReserve = 120; // Reserve space for name column (will be flexible)
          const availableWidth = pageWidth - (left * 2); // Full width minus margins
          const dateColumnWidth = Math.max(20, (availableWidth - fixedColumnsWidth - nameColumnReserve) / numDateColumns);
          
          // Name column - don't set fixed width, let it be flexible based on content
          baseStyles[0] = { halign: 'left' }; // No cellWidth = flexible
          baseStyles[headerRow1.length - 2] = { cellWidth: 40, halign: 'right' }; // Total column
          baseStyles[headerRow1.length - 1] = { cellWidth: 80, halign: 'center' }; // Approval Status column
          
          // Set width for date columns
          for (let i = 1; i <= numDateColumns; i++) {
            baseStyles[i] = { cellWidth: dateColumnWidth, halign: 'center' };
          }
        } else {
          // Portrait: includes Task column
          baseStyles[0].cellWidth = 90; // Name column
          baseStyles[headerRow1.length - 3] = { cellWidth: 50, halign: 'right' }; // Total column
          baseStyles[headerRow1.length - 2] = { cellWidth: 50, halign: 'center' }; // Task column
          baseStyles[headerRow1.length - 1] = { cellWidth: 90, halign: 'center' }; // Approval Status column
        }
        
        return baseStyles;
      })(),
      tableWidth: orientation === 'landscape' ? pageWidth - (left * 2) : 'auto',
      margin: { left: left, right: pageWidth - right },
      horizontalPageBreak: orientation === 'landscape', // Allow horizontal page breaks in landscape
      overflow: orientation === 'landscape' ? 'linebreak' : 'visible',
      didDrawPage: (data) => {
        // Don't draw full header on subsequent pages - just continue the table
        // The table header will be shown automatically via showHead: 'everyPage'
        if (data.pageNumber > 1) {
          data.cursor.y = 40; // Start near top of page
        }
      },
    });
    
      // Update current Y position for next table
      // Use finalY from autoTable result, which gives the Y position after the table
      if (tableResult && tableResult.finalY !== undefined) {
        // Get the final Y position after the table
        // Reduced spacing to keep more tables on same page
        currentY = tableResult.finalY + 20; // Add spacing between tables (20pt, reduced from 30pt)
        
        // If we're beyond the page, we'll need a new page for next table
        // Be more conservative - only break if really close to bottom
        if (currentY > pageHeight - 30) {
          // Next table will start on new page, so reset position
          currentY = pageHeight; // This will trigger new page in next iteration
        }
      } else {
        // Fallback: estimate position based on table size
        const estimatedEndY = currentY + (tableRows.length + 3) * 25;
        currentY = estimatedEndY + 20; // Add spacing (reduced)
        
        // Check if beyond page - be more conservative
        if (currentY > pageHeight - 30) {
          currentY = pageHeight; // Force new page check
        }
      }
    }); // End of dayRanges.forEach
  }); // End of projectIds.forEach
}

function drawHeader(doc, projectName, startDate, endDate, logoDataUrl, left, right, mm, orientation = 'portrait') {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Logo
  if (logoDataUrl) {
    try {
      const logoWidth = 110;
      const logoHeight = 55;
      const logoX = pageWidth - logoWidth - 40;
      const logoY = 110;
      doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
    } catch (error) {
      console.warn('Failed to add logo to PDF:', error);
    }
  }

  // Title - Top center (moved to top first)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(0);
  doc.text('Consolidate Timesheet', pageWidth / 2, mm(60), { align: 'center' });

  // Header brand line
  doc.setFillColor(9, 109, 217);
  doc.rect(left, mm(90), right - left, 2, 'F');

  // Company info (moved below title)
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(58, 145, 136); // #3A9188
  doc.setFontSize(18);
  doc.text('Primesource Consulting LLP', left, mm(130));
  
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75);
  doc.setFontSize(9);
  doc.text('10/91/K6, Nehru Nager,', left, mm(150));
  doc.text('Surandai Road, Pavoorchatram', left, mm(165));
  doc.text('Tenkasi District - 627806', left, mm(180));

  // Project Name
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.text(`Project Name: ${projectName}`, left, mm(220));
}

